import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { TuiDialogContext, TuiButton, TuiLink, TuiIcon } from '@taiga-ui/core';
import { TuiAvatar } from '@taiga-ui/kit';
import { formatDistanceToNow } from 'date-fns';
import { InputHeading } from '../input-heading/input-heading';
import { TuiHeader } from '@taiga-ui/layout';

export interface DeviceData {
  id: string;
  session_id: string;
  device: {
    name: string;
    browser: string;
    ip: string;
    icon: string;
  };
  location: string;
  firstSignIn: Date;
  lastActivity: Date;
  isActive: boolean;
  isCurrent: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TuiLink,
    TuiButton,
    TuiIcon,
    TuiAvatar,
    InputHeading,
    TuiHeader
  ],
  templateUrl: './sign-out-confirmation-dialog.html',
  styleUrl: './sign-out-confirmation-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignOutConfirmationDialog {
  public readonly context = inject<TuiDialogContext<boolean, any>>(POLYMORPHEUS_CONTEXT);

  routes = {
    Link: '/help-center/activities',
  };

  protected get item(): DeviceData | any {
    const data = this.context.data;
    if (!data) return null;
    // Handle both direct and session-wrapped data
    return data.session || data;
  }

  protected get relativeLastActivity(): string {
    const activity = this.item?.lastActivity;
    if (!activity) return 'Recently';
    try {
      return formatDistanceToNow(new Date(activity), { addSuffix: true });
    } catch (e) {
      return 'Recently';
    }
  }

  protected get firstSignInDate(): string {
    const signIn = this.item?.firstSignIn;
    if (!signIn) return 'Unknown';
    try {
      const date = new Date(signIn);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return 'Unknown';
    }
  }

  protected get isNew(): boolean {
    try {
      const signIn = this.item?.firstSignIn;
      if (!signIn) return false;
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(signIn) > sevenDaysAgo;
    } catch (e) {
      return false;
    }
  }



  protected get browserIcon(): string {
    const browser = (this.item?.device?.browser || '').toLowerCase();
    if (browser.includes('chrome')) return '/assets/browser_icon/chrome.png';
    if (browser.includes('firefox')) return '/assets/browser_icon/firefox.png';
    if (browser.includes('safari')) return '/assets/browser_icon/safari.png';
    if (browser.includes('edge') || browser.includes('microsoft')) return '/assets/browser_icon/microsoft.png';
    if (browser.includes('opera')) return '/assets/browser_icon/opera.png';
    if (browser.includes('brave')) return '/assets/browser_icon/brave.png';
    if (browser.includes('samsung')) return '/assets/browser_icon/Samsung_Internet_logo.png';
    return '/assets/browser_icon/chrome.png'; // Default
  }

  public signOut(): void {
    this.context.completeWith(true);
  }

  public close(): void {
    this.context.completeWith(false);
  }
}
